import React from "react";
import { Text } from "@vibe/core";
import { EMPTY_VALUE_LABEL, isEmptyText, joinNames, plural } from "./resyncCopy";

/**
 * One board's current value for the column being previewed.
 */
export interface PreviewBoardRow {
  boardId: string;
  boardName: string;
  /** What the cell holds now. `null` or blank means the cell is empty. */
  currentText: string | null;
  /** The column (or the subitem itself) does not exist on this board, so nothing is written there. */
  missing?: boolean;
}

/**
 * Everything the before/after table needs, in the same shape for the Items tab (where values
 * come from Monday's activity log lookup) and the Subitems tab (where they come off the scan
 * row). The caller decides which board holds the latest change — the ENGINE's answer on the
 * subitem side, the activity-log winner on the item side — this component never recomputes it.
 */
export interface ResyncPreview {
  columnTitle: string;
  /** The board whose value is copied to the others. Null when no board holds a value. */
  latestBoardId: string | null;
  /** When that board's value was last changed, already formatted for display. */
  latestChangedAt: string | null;
  rows: PreviewBoardRow[];
}

const sameText = (a: string | null, b: string | null): boolean =>
  isEmptyText(a) && isEmptyText(b) ? true : (a ?? "").trim() === (b ?? "").trim();

/** The winner's text — the value every other board will end up with. */
export const previewLatestText = (preview: ResyncPreview): string | null =>
  preview.rows.find((row) => row.boardId === preview.latestBoardId)?.currentText ?? null;

/** True when the latest change was a CLEAR: confirming empties the other boards. */
export const previewIsClear = (preview: ResyncPreview): boolean =>
  preview.latestBoardId !== null && isEmptyText(previewLatestText(preview));

/**
 * The boards the table shows as changing. A DISPLAY derivation: a board already holding the
 * winner's text is shown as "No change" so the user is not told a value will be overwritten by
 * itself. The write itself is planned by the engine from the same values, and a board this
 * marks unchanged writes an identical value at worst — never a different one.
 */
export const previewTargets = (preview: ResyncPreview): PreviewBoardRow[] => {
  if (preview.latestBoardId === null) return [];
  const latestText = previewLatestText(preview);
  return preview.rows.filter(
    (row) => row.boardId !== preview.latestBoardId && !row.missing && !sameText(row.currentText, latestText),
  );
};

/** "Update 2 boards", or "Clear on 1 board" when the latest change was a clear. */
export const previewActionLabel = (preview: ResyncPreview): string => {
  const targets = previewTargets(preview);
  if (targets.length === 0) return "Confirm";
  return previewIsClear(preview)
    ? `Clear on ${plural(targets.length, "board")}`
    : `Update ${plural(targets.length, "board")}`;
};

const TABLE_STYLE: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const HEAD_CELL_STYLE: React.CSSProperties = {
  textAlign: "left",
  fontSize: 11,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--secondary-text-color)",
  fontWeight: 600,
  padding: "6px var(--spacing-small)",
  borderBottom: "1px solid var(--layout-border-color)",
};

const CELL_STYLE: React.CSSProperties = {
  padding: "var(--spacing-small)",
  borderBottom: "1px solid var(--layout-border-color)",
  verticalAlign: "top",
};

const LATEST_TAG_STYLE: React.CSSProperties = {
  display: "inline-block",
  marginTop: 2,
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontWeight: 600,
  color: "var(--positive-color)",
};

const WARNING_STYLE: React.CSSProperties = {
  padding: "var(--spacing-small) var(--spacing-medium)",
  borderRadius: "var(--border-radius-small)",
  background: "var(--warning-color-selected)",
  color: "var(--primary-text-color)",
};

/**
 * A value as the table shows it: the text, or the word "Empty" — never a blank cell. Blank was
 * exactly what the Items tab rendered when the latest change had cleared a value, and a user
 * confirming a blank preview had no way to tell "this clears the column" from "nothing loaded".
 */
const ValueText = ({ text, struck = false }: { text: string | null; struck?: boolean }) =>
  isEmptyText(text) ? (
    <Text type="text2" color="secondary" element="span">
      <em>{EMPTY_VALUE_LABEL}</em>
    </Text>
  ) : (
    <Text
      type="text2"
      weight={struck ? "normal" : "medium"}
      color={struck ? "secondary" : "primary"}
      element="span"
      style={struck ? { textDecoration: "line-through" } : undefined}
    >
      {text}
    </Text>
  );

/**
 * The single-column resync preview: every board, what it holds now, what it will hold after.
 *
 * ONE shape for both tabs (Mike, 2026-09-04, option D1). The row of the latest change is tagged
 * "Latest change" — not "source", which describes the mechanism rather than the reason that
 * board wins.
 */
const ResyncPreviewTable = ({ preview, note }: { preview: ResyncPreview; note?: React.ReactNode }) => {
  const latest = preview.rows.find((row) => row.boardId === preview.latestBoardId) ?? null;
  const latestText = previewLatestText(preview);
  const isClear = previewIsClear(preview);
  const targets = previewTargets(preview);
  const removed = isClear ? targets.filter((row) => !isEmptyText(row.currentText)) : [];

  const when = preview.latestChangedAt ? `, ${preview.latestChangedAt}` : "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
      {latest === null ? (
        <Text type="text1" element="p">
          {`No board holds a value for ${preview.columnTitle}, so there is nothing to copy.`}
        </Text>
      ) : isClear ? (
        <Text type="text1" element="p">
          The most recent change was on <strong>{latest.boardName}</strong>
          {when}, and it cleared {preview.columnTitle}. The other boards will be cleared to match.
        </Text>
      ) : (
        <Text type="text1" element="p">
          The most recent change was on <strong>{latest.boardName}</strong>
          {when}. That value will be copied to the other boards.
        </Text>
      )}

      <div
        style={{
          overflowX: "auto",
          border: "1px solid var(--layout-border-color)",
          borderRadius: "var(--border-radius-medium)",
        }}
      >
        <table style={TABLE_STYLE}>
          <thead>
            <tr>
              <th style={HEAD_CELL_STYLE}>Board</th>
              <th style={HEAD_CELL_STYLE}>{`${preview.columnTitle} now`}</th>
              <th style={HEAD_CELL_STYLE}>After sync</th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row, index) => {
              const isLatest = row.boardId === preview.latestBoardId;
              const willChange = targets.some((target) => target.boardId === row.boardId);
              const last = index === preview.rows.length - 1;
              const cell = last ? { ...CELL_STYLE, borderBottom: "none" } : CELL_STYLE;
              return (
                <tr key={row.boardId} data-latest={isLatest ? "true" : undefined}>
                  <td style={cell}>
                    <Text type="text2" element="div">
                      {row.boardName}
                    </Text>
                    {isLatest ? <span style={LATEST_TAG_STYLE}>Latest change</span> : null}
                  </td>
                  <td style={cell}>
                    {row.missing ? (
                      <Text type="text2" color="secondary" element="span">
                        Not on this board
                      </Text>
                    ) : (
                      <ValueText text={row.currentText} struck={willChange} />
                    )}
                  </td>
                  <td style={cell}>
                    {willChange ? (
                      isClear ? (
                        <Text type="text2" color="secondary" element="span">
                          <em>{`${EMPTY_VALUE_LABEL} (cleared)`}</em>
                        </Text>
                      ) : (
                        <ValueText text={latestText} />
                      )
                    ) : (
                      <Text type="text2" color="secondary" element="span">
                        No change
                      </Text>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {removed.length > 0 && latest ? (
        <div style={WARNING_STYLE} role="note">
          <Text type="text2" element="p">
            {`This removes ${joinNames(
              removed.map((row) => `${row.currentText} from ${row.boardName}`),
            )}. If that was not intended, set ${preview.columnTitle} on ${latest.boardName} first, then sync.`}
          </Text>
        </div>
      ) : null}

      {note}
    </div>
  );
};

export default ResyncPreviewTable;
